import { useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { PriceChart, type CostLine, type ChartFeatures } from "./PriceChart";
import { TimeframeTabs } from "./TimeframeTabs";
import { IntervalTabs } from "./IntervalTabs";
import { ChartControls } from "./ChartControls";
import { PortfolioCards } from "./PortfolioCards";
import { EditAccountsDialog } from "./EditAccountsDialog";
import { NewsFeed } from "./NewsFeed";
import { AiAnalysisPanel } from "./AiAnalysisPanel";
import { LiveBadge } from "./LiveBadge";
import { BidAsk } from "./BidAsk";
import { StatsBar } from "./StatsBar";
import { useApi } from "@/hooks/useApi";
import { useLiveQuote } from "@/hooks/useLiveQuote";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  resolveInterval,
  type CandlesResponse,
  type IndicatorsResponse,
  type Interval,
  type Portfolio,
  type Timeframe,
} from "@/lib/types";
import { fmtPrice } from "@/lib/format";

const COST_LINE_COLORS = ["#3b82f6", "#f59e0b", "#a855f7", "#06b6d4"];

const DEFAULT_FEATURES: ChartFeatures = {
  volume: true,
  ema: true,
  rsi: true,
  macd: true,
  signals: true,
  costLines: true,
};

export function Dashboard({ slug, onNavigateHome }: { slug: string; onNavigateHome: () => void }) {
  const [editing, setEditing] = useState(false);

  // Period + interval are persisted so refreshing keeps your view.
  const [tf, setTf] = useLocalStorage<Timeframe>("terranet.period", "month");
  const [storedInterval, setStoredInterval] = useLocalStorage<Interval>("terranet.interval", "1d");
  const [features, setFeatures] = useLocalStorage<ChartFeatures>("terranet.chartFeatures", DEFAULT_FEATURES);

  // Clamp in case a previously stored period/interval combo is no longer valid.
  const interval = resolveInterval(tf, storedInterval);

  // Changing period clamps the interval to one valid for that period.
  const changePeriod = (next: Timeframe) => {
    setTf(next);
    setStoredInterval((cur) => resolveInterval(next, cur));
  };

  const { quote: live, connected } = useLiveQuote();
  const candles = useApi<CandlesResponse>(`/api/candles?tf=${tf}&interval=${interval}`, 60_000);
  const indicators = useApi<IndicatorsResponse>(`/api/indicators?tf=${tf}&interval=${interval}`, 60_000);
  const portfolio = useApi<Portfolio>(`/api/portfolios/${slug}`, 30_000);

  const price = live?.price ?? candles.data?.candles.at(-1)?.c ?? null;

  // Cost-basis lines derive from the portfolio (single source of truth).
  const costLines: CostLine[] = useMemo(
    () =>
      (portfolio.data?.accounts ?? []).map((a, i) => ({
        price: a.avgCost,
        label: `${a.label} ${a.avgCost.toFixed(2).replace(".", ",")}`,
        color: COST_LINE_COLORS[i % COST_LINE_COLORS.length],
      })),
    [portfolio.data],
  );

  const toggle = (key: keyof ChartFeatures) => setFeatures((f) => ({ ...f, [key]: !f[key] }));

  // Unknown slug → the API 404s and `fetchJson` throws "not_found".
  if (portfolio.error === "not_found") {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-2xl font-bold">Portföljen hittades inte</h1>
        <p className="text-sm text-muted-foreground">Länken är ogiltig eller har tagits bort.</p>
        <Button onClick={onNavigateHome}>Skapa en ny portfölj</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">TERRNT B</span>
            <span>·</span>
            <span>Terranet B · First North Stockholm</span>
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <h1 className="text-4xl font-bold tabular-nums">
              {price != null ? fmtPrice(price) : <Skeleton className="inline-block h-9 w-28" />}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <LiveBadge connected={connected} quote={live} />
          <BidAsk quote={live} />
        </div>
      </header>

      {/* Key stats */}
      <StatsBar quote={live} />

      {/* Portfolio */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">Portfölj</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            disabled={!portfolio.data}
          >
            <Pencil className="size-3.5" />
            Redigera konton
          </Button>
        </div>
        <PortfolioCards portfolio={portfolio.data} loading={portfolio.loading} />
      </div>

      {editing && portfolio.data && (
        <EditAccountsDialog
          slug={slug}
          accounts={portfolio.data.accounts}
          onClose={() => setEditing(false)}
          onSaved={portfolio.refetch}
        />
      )}

      {/* Chart */}
      <Card>
        <CardHeader className="gap-3 pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TimeframeTabs value={tf} onChange={changePeriod} />
            <div className="flex items-center gap-3">
              {candles.data?.stale && <span className="text-xs text-amber-500">cachelagrad data</span>}
              <IntervalTabs timeframe={tf} value={interval} onChange={setStoredInterval} />
            </div>
          </div>
          <ChartControls features={features} onToggle={toggle} />
        </CardHeader>
        <CardContent className="pt-2">
          {candles.error ? (
            <div className="flex h-[460px] items-center justify-center text-sm text-muted-foreground">
              Kunde inte ladda kursdata: {candles.error}
            </div>
          ) : candles.loading && !candles.data ? (
            <Skeleton className="h-[460px] w-full" />
          ) : (
            <PriceChart
              candles={candles.data?.candles ?? []}
              indicators={indicators.data}
              costLines={costLines}
              features={features}
            />
          )}
        </CardContent>
      </Card>

      {/* News + AI */}
      <div className="grid gap-6 lg:grid-cols-2">
        <NewsFeed />
        <AiAnalysisPanel />
      </div>

      <footer className="pb-4 text-center text-xs text-muted-foreground">
        Data från Avanza (primär) och Yahoo Finance (reserv). Ej finansiell rådgivning.
      </footer>
    </div>
  );
}
